/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { SystemTimestampType } from 'src/graphql/generated/graphql.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TimestampTypeProps {
  id: string;
  name: string;
  systemTimestampType: SystemTimestampType | null;
  order: number;
  description: string | null;
  companies: List<IdListItemProps> | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TimestampTypeRecord extends Record<TimestampTypeProps>({
  id: null as any,
  name: null as any,
  systemTimestampType: null as any,
  order: null as any,
  description: null as any,
  companies: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    systemTimestampType: SystemTimestampType | null;
    order: number;
    description: string | null;
    companies: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TimestampTypeRecord {
    if (json == null) {
      throw new Error('TimestampTypeRecord.fromJS: parameter cannot be null');
    }
    return new TimestampTypeRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TimestampTypeJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'TimestampTypeJson.name'),
      systemTimestampType: json.systemTimestampType,
      order: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'order', 0),
        'TimestampTypeJson.order',
      ),
      description: json.description,
      companies:
        json.companies != null
          ? List<IdListItemRecord>(json.companies.map((o: any) => new IdListItemRecord(o)))
          : null,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    systemTimestampType: SystemTimestampType | null;
    order: number;
    description: string | null;
    companies: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TimestampTypeRecord, Error[]] {
    if (json == null) {
      throw new Error('TimestampTypeRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TimestampTypeRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TimestampTypeJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'TimestampTypeJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      systemTimestampType: tryOrDefault(
        () => json.systemTimestampType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      order: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'order', 0), 'TimestampTypeJson.order'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: tryOrDefault(
        () => json.description,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companies: tryOrDefault(
        () =>
          json.companies != null
            ? List<IdListItemRecord>(json.companies.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TimestampTypeProps) {
    super(props);
    if (props == null) {
      throw new Error('TimestampTypeRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TimestampTypeViewModelBase implements TimestampTypeProps {
  protected readonly _props: TimestampTypeProps;
  private _json?: TimestampTypeProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get systemTimestampType(): SystemTimestampType | null {
    return this._props.systemTimestampType;
  }
  get order(): number {
    return this._props.order;
  }
  get description(): string | null {
    return this._props.description;
  }
  get companies(): List<IdListItemProps> | null {
    return this._props.companies;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isArriveBase() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveBase;
  }
  public get isArriveBedsideReceiving() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveBedsideReceiving;
  }
  public get isArriveDropoff() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveDropoff;
  }
  public get isArriveDropoffLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveDropoffLzViaGround;
  }
  public get isArrivePatient() {
    return this._props.systemTimestampType === SystemTimestampType.ArrivePatient;
  }
  public get isArrivePickup() {
    return this._props.systemTimestampType === SystemTimestampType.ArrivePickup;
  }
  public get isArrivePickupLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArrivePickupLzViaGround;
  }
  public get isArriveReceivingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveReceivingViaGround;
  }
  public get isArriveSendingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveSendingViaGround;
  }
  public get isArriveWaypoint() {
    return this._props.systemTimestampType === SystemTimestampType.ArriveWaypoint;
  }
  public get isAssignmentStarted() {
    return this._props.systemTimestampType === SystemTimestampType.AssignmentStarted;
  }
  public get isCardClosed() {
    return this._props.systemTimestampType === SystemTimestampType.CardClosed;
  }
  public get isConfirmedDropoffGround() {
    return this._props.systemTimestampType === SystemTimestampType.ConfirmedDropoffGround;
  }
  public get isConfirmedPickupGround() {
    return this._props.systemTimestampType === SystemTimestampType.ConfirmedPickupGround;
  }
  public get isCrewNotificationReceived() {
    return this._props.systemTimestampType === SystemTimestampType.CrewNotificationReceived;
  }
  public get isCrewNotified() {
    return this._props.systemTimestampType === SystemTimestampType.CrewNotified;
  }
  public get isCustom() {
    return this._props.systemTimestampType === SystemTimestampType.Custom;
  }
  public get isDepartBase() {
    return this._props.systemTimestampType === SystemTimestampType.DepartBase;
  }
  public get isDepartBedsideReceiving() {
    return this._props.systemTimestampType === SystemTimestampType.DepartBedsideReceiving;
  }
  public get isDepartDropoff() {
    return this._props.systemTimestampType === SystemTimestampType.DepartDropoff;
  }
  public get isDepartDropoffLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartDropoffLzViaGround;
  }
  public get isDepartPickup() {
    return this._props.systemTimestampType === SystemTimestampType.DepartPickup;
  }
  public get isDepartPickupLzViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartPickupLzViaGround;
  }
  public get isDepartReceivingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartReceivingViaGround;
  }
  public get isDepartSendingViaGround() {
    return this._props.systemTimestampType === SystemTimestampType.DepartSendingViaGround;
  }
  public get isDepartWaypoint() {
    return this._props.systemTimestampType === SystemTimestampType.DepartWaypoint;
  }
  public get isDepartWithPatient() {
    return this._props.systemTimestampType === SystemTimestampType.DepartWithPatient;
  }
  public get isEmsReport() {
    return this._props.systemTimestampType === SystemTimestampType.EmsReport;
  }
  public get isEnRouteToPatient() {
    return this._props.systemTimestampType === SystemTimestampType.EnRouteToPatient;
  }
  public get isEnRouteToPickup() {
    return this._props.systemTimestampType === SystemTimestampType.EnRouteToPickup;
  }
  public get isEtaProvided() {
    return this._props.systemTimestampType === SystemTimestampType.EtaProvided;
  }
  public get isExternalActionCancellationRequested() {
    return (
      this._props.systemTimestampType === SystemTimestampType.ExternalActionCancellationRequested
    );
  }
  public get isExternalActionRemoveStandby() {
    return this._props.systemTimestampType === SystemTimestampType.ExternalActionRemoveStandby;
  }
  public get isExternalRequestDisregarded() {
    return this._props.systemTimestampType === SystemTimestampType.ExternalRequestDisregarded;
  }
  public get isFboConfirmed() {
    return this._props.systemTimestampType === SystemTimestampType.FboConfirmed;
  }
  public get isFollowUpWeatherCheck() {
    return this._props.systemTimestampType === SystemTimestampType.FollowUpWeatherCheck;
  }
  public get isInitialDeparture() {
    return this._props.systemTimestampType === SystemTimestampType.InitialDeparture;
  }
  public get isManifestConfirmed() {
    return this._props.systemTimestampType === SystemTimestampType.ManifestConfirmed;
  }
  public get isMedicalControlContactAttempted() {
    return this._props.systemTimestampType === SystemTimestampType.MedicalControlContactAttempted;
  }
  public get isOperationCancelled() {
    return this._props.systemTimestampType === SystemTimestampType.OperationCancelled;
  }
  public get isOperationCompleted() {
    return this._props.systemTimestampType === SystemTimestampType.OperationCompleted;
  }
  public get isOverheadDropoff() {
    return this._props.systemTimestampType === SystemTimestampType.OverheadDropoff;
  }
  public get isOverheadPickup() {
    return this._props.systemTimestampType === SystemTimestampType.OverheadPickup;
  }
  public get isPaipEvent() {
    return this._props.systemTimestampType === SystemTimestampType.PaipEvent;
  }
  public get isPatientInfoMessageSent() {
    return this._props.systemTimestampType === SystemTimestampType.PatientInfoMessageSent;
  }
  public get isPlacedOnStandby() {
    return this._props.systemTimestampType === SystemTimestampType.PlacedOnStandby;
  }
  public get isReceivingFacilityRequestAccepted() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestAccepted;
  }
  public get isReceivingFacilityRequestCancelled() {
    return (
      this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestCancelled
    );
  }
  public get isReceivingFacilityRequestDenied() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestDenied;
  }
  public get isReceivingFacilityRequestDiverted() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestDiverted;
  }
  public get isReceivingFacilityRequestReferred() {
    return this._props.systemTimestampType === SystemTimestampType.ReceivingFacilityRequestReferred;
  }
  public get isReferRequest() {
    return this._props.systemTimestampType === SystemTimestampType.ReferRequest;
  }
  public get isRequestCancelled() {
    return this._props.systemTimestampType === SystemTimestampType.RequestCancelled;
  }
  public get isRequestReactivated() {
    return this._props.systemTimestampType === SystemTimestampType.RequestReactivated;
  }
  public get isRequestReceived() {
    return this._props.systemTimestampType === SystemTimestampType.RequestReceived;
  }
  public get isResendToDW() {
    return this._props.systemTimestampType === SystemTimestampType.ResendToDW;
  }
  public get isSentToImageTrend() {
    return this._props.systemTimestampType === SystemTimestampType.SentToImageTrend;
  }
  public get isStandbyRemoved() {
    return this._props.systemTimestampType === SystemTimestampType.StandbyRemoved;
  }
  public get isTransportCheckInitiated() {
    return this._props.systemTimestampType === SystemTimestampType.TransportCheckInitiated;
  }
  public get isUnitAborted() {
    return this._props.systemTimestampType === SystemTimestampType.UnitAborted;
  }
  public get isUnitActivated() {
    return this._props.systemTimestampType === SystemTimestampType.UnitActivated;
  }
  public get isUnitAssigned() {
    return this._props.systemTimestampType === SystemTimestampType.UnitAssigned;
  }
  public get isUnitOffered() {
    return this._props.systemTimestampType === SystemTimestampType.UnitOffered;
  }
  public get isWeatherDeclined() {
    return this._props.systemTimestampType === SystemTimestampType.WeatherDeclined;
  }
  public get isWeatherDelayed() {
    return this._props.systemTimestampType === SystemTimestampType.WeatherDelayed;
  }
  public get isWebOrderAcknowledged() {
    return this._props.systemTimestampType === SystemTimestampType.WebOrderAcknowledged;
  }

  public isAnySystemTimestampType(...systemTimestampType: SystemTimestampType[]) {
    return (
      this._props.systemTimestampType != null &&
      systemTimestampType.includes(this._props.systemTimestampType)
    );
  }

  constructor(props: TimestampTypeProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        systemTimestampType: this._props.systemTimestampType,
        order: this._props.order,
        description: this._props.description,
        companies: this._props.companies,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTimestampTypeEntities = (state: AppState) =>
  state.entityStores.timestampType.entities;

export const selectTimestampTypeById = (id: string) => (state: AppState) =>
  state.entityStores.timestampType.entities.get(id);

export const selectTimestampTypeMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.timestampType.meta.get(id);

export const selectTimestampTypeAndMetadataById = (id: string) =>
  createSelector(
    selectTimestampTypeById(id),
    selectTimestampTypeMetadataById(id),
    (timestampType, meta) => ({ timestampType, meta }),
  );

export function selectTimestampTypeEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTimestampTypeEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TimestampTypeRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTimestampTypeEntitiesByName(name: string) {
  return createSelector(selectAllTimestampTypeEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectTimestampTypeEntitiesBySystemTimestampType(
  systemTimestampType: SystemTimestampType,
) {
  return createSelector(selectAllTimestampTypeEntities, (entities) =>
    entities.filter((e) => e.systemTimestampType === systemTimestampType).valueSeq(),
  );
}

export function selectTimestampTypeEntitiesByOrder(order: number) {
  return createSelector(selectAllTimestampTypeEntities, (entities) =>
    entities.filter((e) => e.order === order).valueSeq(),
  );
}

export function selectTimestampTypeEntitiesByDescription(description: string) {
  return createSelector(selectAllTimestampTypeEntities, (entities) =>
    entities.filter((e) => e.description === description).valueSeq(),
  );
}

//#endregion Selectors
